import {Injectable} from '@angular/core';
import {AngularFirestore, QueryFn} from '@angular/fire/compat/firestore';
import {TenantService} from './tenant.service';
import {from, Observable} from 'rxjs';
import {AssignmentSolution} from '../models/assignment-solution.model';
import {map} from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class AssignmentSolutionsDbService {

  constructor(private afs: AngularFirestore,
              private tenant: TenantService) {
  }


  assignmentSolutionsPath() {
    return `schools/${this.tenant.id}/assignmentSolutions`;
  }


  createNewAssigmentSolution(solution: Partial<AssignmentSolution>): Observable<AssignmentSolution> {

    const assigmentSolutionId = this.afs.createId();

    const assignmentSolutionPath = `${this.assignmentSolutionsPath()}/${assigmentSolutionId}`;

    return from(this.afs.doc(assignmentSolutionPath).set(solution))
      .pipe(
        map(() => {
          return <AssignmentSolution>{
            id: assigmentSolutionId,
            ...solution
          };
        })
      );
  }

  /**
   *
   * A user might load several different versions of a solution for the same assignment over time.
   *
   * This query makes sure that we only get the latest uploaded solution for that user and assignment lesson,
   *
   * or null of no available solution is available.
   *
   */

  loadLatestAssignmentSolutionForLesson(lessonId: string, userId: string): Observable<AssignmentSolution> {
    return this.afs.collection(`${this.assignmentSolutionsPath()}`,
      ref => ref.where('lessonId', '==', lessonId)
        .where('userId', '==', userId)
        .orderBy('submittedAt', 'desc')
        .limit(1)
    )
      .get()
      .pipe(
        map(results => {

          if (results.empty) {
            return null;
          }

          const snap = results.docs[0],
            data: any = snap.data();

          return <AssignmentSolution>{
            id: snap.id,
            ...data
          };
        })
      );
  }


  loadAllAssignmentsSolution(pageSize: number, startAfter?) {

    const queryFn: QueryFn = ref => {
      let query = ref.orderBy("submittedAt", "desc").limit(pageSize);
      if (startAfter) {
        query = query.startAfter(startAfter);
      }
      return query;
    };

    return this.afs.collection(this.assignmentSolutionsPath(), queryFn)
      .get()
      .pipe(
        map(results => {

          return results.docs.map(result => {

            const data: any = result.data(),
              id = result.id;

            return <AssignmentSolution>{
              id,
              ...data
            };

          });
        })
      );
  }

  updateAssignmentSolutionStatus(solution: AssignmentSolution, status: string) {

    const {userId, courseId, lessonId} = solution;

    const batch = this.afs.firestore.batch();

    const assignmentSolutionRef = this.afs.doc(`${this.assignmentSolutionsPath()}/${solution.id}`).ref;

    batch.update(assignmentSolutionRef, {status});

    if (status == "Approved") {

      const lessonStatusRef = this.afs.doc(`schools/${this.tenant.id}/users/${userId}/lessonsWatchedPerCourse/${courseId}/lessonsWatched/${lessonId}`).ref;

      batch.set(lessonStatusRef, {
        watched:true
      },
        {
          merge:true
        });

    }

    return batch.commit();
  }

  createNewAssignmentMessage(id: string, message: {}) {
    const assigmentMessageId = this.afs.createId();
    return this.afs.doc(`${this.assignmentSolutionsPath()}/${id}/assignmentMessages/${assigmentMessageId}`).set({message});
  }

}
